import type { ReactNode } from 'react';
import { motion, useReducedMotion, type HTMLMotionProps } from 'motion/react';
import { cn } from '../utils/cn';
import { badgeSizeStyles, badgeToneStyles, type BadgeVariantType } from '../config/badge';
import {
  type EssenceType,
  type LegacyToneType,
  type SizeType,
  type ToneType,
  resolveLegacyTone,
} from '../config/types';

interface BadgeProps extends Omit<HTMLMotionProps<'span'>, 'color'> {
  tone?: ToneType;
  variant?: BadgeVariantType;
  size?: SizeType;
  icon?: ReactNode;
  iconPosition?: 'start' | 'end';
  isInteractive?: boolean;
  isDisabled?: boolean;
  // Backward compatibility aliases
  color?: LegacyToneType;
  essence?: EssenceType;
  children?: ReactNode;
}

function Badge({
  className,
  tone,
  color = 'neutral',
  variant,
  essence,
  size = 'small',
  icon,
  iconPosition = 'start',
  isInteractive = false,
  isDisabled = false,
  children,
  ...props
}: BadgeProps) {
  const reduceMotion = useReducedMotion();
  const resolvedTone = resolveLegacyTone(tone, color);
  const resolvedVariant =
    variant ?? (essence === 'dark' ? 'solid' : essence === 'light' ? 'subtle' : 'subtle');
  const interactive = isInteractive && !isDisabled;

  return (
    <motion.span
      data-slot="badge"
      whileTap={interactive && !reduceMotion ? { scale: 0.97 } : undefined}
      transition={{ duration: 0.12, ease: 'easeOut' }}
      className={cn(
        'group/badge inline-flex w-fit shrink-0 items-center justify-center overflow-hidden rounded-full border font-medium whitespace-nowrap transition-colors [&>svg]:pointer-events-none [&>svg]:size-3',
        badgeSizeStyles[size],
        badgeToneStyles[resolvedVariant][resolvedTone],
        interactive ? 'cursor-pointer' : 'cursor-default',
        isDisabled ? 'cursor-not-allowed opacity-50' : '',
        className,
      )}
      aria-disabled={isDisabled || undefined}
      {...props}
    >
      {icon && iconPosition === 'start' ? icon : null}
      {children}
      {icon && iconPosition === 'end' ? icon : null}
    </motion.span>
  );
}

export { Badge };
export type { BadgeVariantType };
